import { z } from "zod";
import { MicroSchema } from "./money.js";
import { Brief } from "./brief.js";
import { Script } from "./script.js";
import { Region } from "./tour.js";
import { StudioTurnInput, StudioTurnOutput } from "./studioTurn.js";
import { HelpAnswerInput, HelpModelAnswer } from "./help/assistant.js";
import { WebsitePage } from "./website.js";
import { ArticleDraft, ArticleIntent, ArticleSource, SourceRef, TopicPlan } from "./article.js";
import { WeekPlan, WeekPlanInput } from "./week.js";
import { MarketingReviewInput, MarketingReviewOutput } from "./review.js";
import { ReviewTurnInput, ReviewTurnOutput } from "./conversation.js";
import { DraftPostsInput, DraftPostsOutput } from "./drafter.js";

/** Operations the pipeline knows. Vendors are behind them (04). */
export const Operation = z.enum([
  "studio_turn",
  "help_answer",
  "write_script",
  "describe_image",
  "edit_image",
  "generate_image",
  "image_to_video",
  "tts",
  "align",
  "music",
  "lookup_place",
  "place_photo",
  "find_competitors",
  "find_images",
  "read_site",
  "pick_pages",
  "fetch_page",
  "search_images",
  "read_serp",
  "plan_topics",
  "write_article",
  "translate_article",
  "render",
  // Customers (23): finding a business its next customer. Four places a model or a paid search is used, and only
  // four — everything between them is code and the workspace's own scored table.
  "outreach_setup",
  "find_companies",
  "outreach_find",
  "outreach_judge",
  "outreach_opener",
  // The fifth: one call a night that reads what came back and proposes at most four changes (ADR 0039).
  "outreach_learn",
  // The one page a business with no website gets, written in words only (ADR 0047, 28).
  "write_site",
  // A week of posts proposed from the business page and its photos, each made later by Generate (ADR 0023, 03).
  "plan_week",
  // The marketing review and the conversation about it (ADR 0063, 35): one proposal from the evidence code gathered,
  // and a turn that answers a question about it. Answered by the assistant the workspace chose, like the Studio guide.
  "marketing_review",
  "review_turn",
  // Posts written from what the business already says, as drafts an owner approves (31, ADR 0056). Answered by the
  // assistant the workspace chose, and asked for by the overnight preparer within the owner's weekly number (ADR 0064).
  "draft_posts",
]);
export type Operation = z.infer<typeof Operation>;

/** A file a provider produced or consumed, by storage key. Only the storage module knows the layout. */
export const AssetRef = z.object({
  key: z.string().min(1),
  mime: z.string().min(1),
  bytes: z.number().int().min(0),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  durationMs: z.number().int().min(0).optional(),
  sha256: z.string().length(64).optional(),
});
export type AssetRef = z.infer<typeof AssetRef>;

/** A word of narration and when it is said, in ms from the start of its line. */
export const SpokenWord = z.object({ text: z.string(), startMs: z.number().int(), endMs: z.number().int() });
export type SpokenWord = z.infer<typeof SpokenWord>;

const Size = z.object({ width: z.number().int().positive(), height: z.number().int().positive() });

const Review = z.object({ rating: z.number(), text: z.string() });
const Rival = z.object({ name: z.string(), category: z.string(), rating: z.number().optional(), ratingCount: z.number().int().optional() });

/**
 * One listing on Google Maps, as `lookup_place` reads it.
 *
 * A **listing**, not a place: every screen in this app already says "the Google listing", and since ADR 0043 a
 * place is a country or a town — where a workspace looks, which is a different thing entirely.
 */
const Listing = z.object({
  placeId: z.string(),
  name: z.string(),
  address: z.string(),
  phone: z.string().optional(),
  website: z.url().optional(),
  hours: z.array(z.string()).optional(),
  /** "Cafe", "Pet Care": Google's words for what it is. */
  type: z.string(),
  rating: z.number().optional(),
  ratingCount: z.number().int().optional(),
  /** "inexpensive", or empty. */
  price: z.string(),
  mapsUrl: z.string(),
  /** Google's own line about it, or its summary of the reviews. */
  summary: z.string(),
  /** "takeaway", "dogs welcome": what the listing says yes to. */
  amenities: z.array(z.string()),
  reviews: z.array(Review).max(5),
  photos: z.array(z.object({ name: z.string(), width: z.number().int(), height: z.number().int(), author: z.string() })).max(10),
});
export type Listing = z.infer<typeof Listing>;

export const OperationIO = {
  /**
   * One turn of the Studio conversation (ADR 0031, `studioTurn.ts`): the business page, the brief, the photo list and
   * what was said in; the reply, what the owner decided and what the guide suggests out. Answered by whichever
   * assistant the workspace chose.
   */
  studio_turn: { input: StudioTurnInput, output: StudioTurnOutput },
  /**
   * One question to the help assistant (docs/19, ADR 0026). Paid for by the app, answered only from the help pages the
   * adapter is given, and read leniently: what reaches a screen is decided by `judgeHelpAnswer`, not by this parse.
   */
  help_answer: { input: HelpAnswerInput, output: HelpModelAnswer },
  write_script: {
    // `profile` is the business's own page, in markdown, exactly as the customer reads it in the Business tab
    // (`profileMarkdown`). It used to be a bag of facts serialised as JSON, which is a worse prompt and a thing
    // nobody could check: the promise the tab makes is that what is on the page is what the model is told.
    input: z.object({ brief: Brief, business: z.object({ name: z.string(), industry: z.string().optional(), profile: z.string().default("") }), photos: z.array(z.object({ id: z.uuid(), caption: z.string(), hasPeople: z.boolean(), regions: z.array(z.object({ n: z.number().int().min(0), label: z.string(), kind: z.string() })).default([]) })) }),
    output: z.object({ script: Script }),
  },
  /** Also where the things worth looking at are, which is what a photo's tour flies to (`tour.ts`). */
  describe_image: { input: z.object({ image: AssetRef }), output: z.object({ caption: z.string(), tags: z.array(z.string()), hasPeople: z.boolean(), regions: z.array(Region).max(8).default([]) }) },
  edit_image: { input: z.object({ source: AssetRef, instruction: z.string(), size: Size }), output: z.object({ image: AssetRef }) },
  generate_image: { input: z.object({ prompt: z.string(), size: Size }), output: z.object({ image: AssetRef }) },
  image_to_video: { input: z.object({ image: AssetRef, motionPrompt: z.string(), durationMs: z.number().int().min(2_000).max(10_000) }), output: z.object({ video: AssetRef }) },
  /**
   * One line of narration. `previousText` and `nextText` are the lines either side of it, so it is said as part of one
   * voice rather than started over at every shot (docs/30 finding 8). `words` is when each word is said, when the
   * vendor gives it; without it the line goes through `align`.
   */
  tts: {
    input: z.object({ text: z.string().min(1), voiceId: z.string(), previousText: z.string().optional(), nextText: z.string().optional() }),
    output: z.object({ audio: AssetRef, words: z.array(SpokenWord).optional() }),
  },
  align: { input: z.object({ audio: AssetRef, text: z.string() }), output: z.object({ words: z.array(SpokenWord) }) },
  music: { input: z.object({ mood: z.string(), durationMs: z.number().int() }), output: z.object({ audio: AssetRef }) },
  /**
   * The business, turned into the answers the profile asks for, from everything the lookup found (ADR 0029): its own
   * site as text, what Google knows and its customers' reviews, and the businesses nearby doing the same thing. With
   * none of it — an idea, or a business with nothing online — the model works from the name and the town alone, and
   * everything it returns is marked as a guess rather than as something read.
   *
   * The name is older than the input: it read the site and nothing else until 2026-09-11, and the rows and prices
   * already say `read_site`.
   */
  read_site: {
    input: z.object({
      name: z.string().min(1), town: z.string().default(""), url: z.string().default(""), text: z.string().default(""),
      stage: z.enum(["open", "idea"]).default("open"),
      /** What Google knows. Reviews are read here and never kept: the section written from them is. */
      place: z.object({
        type: z.string(), rating: z.number().optional(), ratingCount: z.number().int().optional(), price: z.string(),
        summary: z.string(), amenities: z.array(z.string()), reviews: z.array(Review).max(5),
      }).nullable().default(null),
      rivals: z.array(Rival).max(10).default([]),
      /**
       * What the owner tapped they want more of while the lookup read, from chips written before anybody knew the
       * business's language. `works` then says the same, in the page's language (2026-09-25: "Bookings" on a French
       * business page). Empty when they tapped nothing.
       */
      wantMore: z.string().max(400).default(""),
    }),
    output: z.object({
      what: z.string(), who: z.string(), tone: z.string(), works: z.string(), industry: z.string(),
      // The page usually carries these too, and for a business with a site and no map listing they are the only
      // place they exist. Empty when the page does not say.
      where: z.string().default(""), hours: z.string().default(""), links: z.string().default(""),
      /** What the reviews say, empty with no reviews. */
      loved: z.string().default(""),
      /** Who else nearby does the same, and what sets this business apart, empty with nobody found. */
      rivals: z.string().default(""),
      /**
       * Short answers to tap on the check screen (ADR 0049): a few words each, the likeliest first. What the business
       * sells, who buys it, and what it would most want more of.
       */
      options: z.object({
        what: z.array(z.string()).max(6).default([]),
        who: z.array(z.string()).max(6).default([]),
        works: z.array(z.string()).max(6).default([]),
      }).default({ what: [], who: [], works: [] }),
      /** Three ways it could sound, each with one short post written that way, to choose *How we sound* from. */
      voices: z.array(z.object({ name: z.string(), describe: z.string(), sample: z.string() })).max(3).default([]),
    }),
  },
  /**
   * Which of a site's own pages are worth reading, chosen from a list somebody else built.
   *
   * The input is indices, a path and a few words of link text; the output is **indices into that same list**. The
   * model never names a URL and never fetches anything. That is the whole security design: link text is written by
   * whoever owns the site, so it is untrusted input, and the worst a poisoned page can do is make us read a
   * different page of its own that we had already checked and were already willing to fetch.
   */
  pick_pages: {
    input: z.object({
      name: z.string(),
      candidates: z.array(z.object({ index: z.number().int().min(0), path: z.string(), text: z.string() })).max(30),
      most: z.number().int().min(1).max(6),
    }),
    output: z.object({ pick: z.array(z.number().int().min(0)).max(6) }),
  },
  /**
   * A page of the open web, fetched by somebody whose business that is (04).
   *
   * It was our own `fetch` until 2026-09-09, which meant our IP address, our lack of a JavaScript renderer and our
   * absence from anyone's allow-list — and, worse, a fetch nobody could see: it went through no call path, so it
   * was never priced, never capped, never logged, and never appeared in the customer's list of what was done for
   * them. This is that fetch made ordinary.
   */
  fetch_page: {
    input: z.object({ url: z.url(), renderJs: z.boolean().default(false) }),
    output: z.object({ url: z.string(), status: z.number().int(), html: z.string(), text: z.string() }),
  },
  /**
   * Pictures nobody owns yet — the Studio's "Find one", and the same provider the Business tab would use if it
   * ever needed a picture it could not find on the business's own site (03 §photos).
   */
  search_images: {
    input: z.object({ query: z.string().min(1).max(120), count: z.number().int().min(1).max(30).default(12) }),
    output: z.object({
      results: z.array(z.object({
        id: z.string(), url: z.url(), thumbUrl: z.url(), width: z.number().int(), height: z.number().int(),
        /** Who took it. Stock that requires attribution is the only kind worth using, so it travels with the picture. */
        credit: z.string(),
      })),
    }),
  },
  /**
   * The business on Google (ADR 0029): the best match for a name and a town, with everything the listing holds that a
   * post can use. `name` is Google's, so the caller can tell a near miss — "Bramble Bakehouse" in another town — from
   * the business it asked about. Photos are references, fetched one by one with `place_photo`, and `author` says who
   * posted each: only the business's own are worth offering.
   */
  lookup_place: {
    // With a place id, the listing the owner said is theirs is read by id rather than searched for.
    input: z.object({ name: z.string(), town: z.string(), placeId: z.string().min(1).max(300).optional() }),
    output: Listing,
  },
  /** One Google listing photo, as a short-lived address to download it from. Priced per photo. */
  place_photo: {
    input: z.object({ name: z.string().regex(/^places\/[^/]+\/photos\/[^/]+$/), maxPx: z.number().int().min(100).max(4800).default(1600) }),
    output: z.object({ url: z.url() }),
  },
  /** The businesses a search for "bakery in Ledbury" lists: who else a customer could choose. */
  find_competitors: {
    input: z.object({ query: z.string().min(2).max(160) }),
    output: z.object({ places: z.array(Rival.extend({ address: z.string() })).max(20) }),
  },
  /**
   * Pictures of one business on the open web — its Facebook and Instagram posts, its site, the directories that list it
   * — as an image search finds them. The only way to its social pages that is not a login wall. The caller decides which
   * results are about this business.
   */
  find_images: {
    input: z.object({ query: z.string().min(2).max(160) }),
    output: z.object({
      results: z.array(z.object({ url: z.string(), width: z.number().int(), height: z.number().int(), title: z.string(), domain: z.string(), link: z.string() })).max(30),
    }),
  },
  /**
   * What Google already answers for one phrase ([21 §3](../../../docs/21-articles-and-seo.md#3--topic-plan)).
   *
   * One search, one credit, a tenth of a cent. It is the only thing in this product that looks at what is already
   * ranking, and it exists because everything else here was guessing: a beautifully sourced article for a phrase
   * nobody types is a wasted afternoon, and the questions Google lists under a result are the closest thing to
   * hearing the customer's customers ask them.
   *
   * It does **not** answer how many people search for the phrase. Nobody's search API gives that away for a tenth
   * of a cent, and a made-up number would be worse than none — so what comes back is what is really there: who
   * ranks, what they lead with, and what people ask next.
   */
  read_serp: {
    input: z.object({
      phrase: z.string().min(2).max(200),
      /** Two letters, as Google means them: the country the search is run from. */
      country: z.string().regex(/^[a-z]{2}$/).default("gb"),
      language: z.string().regex(/^[a-z]{2}$/).default("en"),
    }),
    output: z.object({
      results: z.array(z.object({
        position: z.number().int().min(1),
        title: z.string(),
        link: z.string(),
        domain: z.string(),
        snippet: z.string(),
      })).max(10),
      /** Google's "people also ask", which is the FAQ somebody else already researched. */
      questions: z.array(z.string()).max(12),
      /** The phrasings Google offers underneath, which is how the same want is said differently. */
      related: z.array(z.string()).max(12),
    }),
  },
  /**
   * Topics worth writing about, for a site that already has some ([21 §3](../../../docs/21-articles-and-seo.md#3--topic-plan)).
   *
   * It is given the business page **without its rivals**, what the business knows for search, the titles and first
   * lines of the sources, and the phrases the site has already been written for, so nothing is proposed twice. It is
   * never given competitor names, because a topic plan is exactly where they would resurface.
   */
  plan_topics: {
    input: z.object({
      business: z.string().default(""),
      audience: z.string().default(""),
      languages: z.array(z.string()).default([]),
      neverTopic: z.array(z.string()).default([]),
      neverClaim: z.array(z.string()).default([]),
      sources: z.array(z.object({ ref: SourceRef, title: z.string(), firstLine: z.string() })).max(40).default([]),
      existing: z.array(z.string()).max(200).default([]),
      /**
       * Phrases Google **already** shows this site for, on page two or three, that nobody clicks
       * ([21 §13](../../../docs/21-articles-and-seo.md#13--what-google-search-console-says)). The best input this
       * operation gets, because it is the only one that is not a guess: the site is being shown and passed over,
       * so the work is already half done.
       */
      alreadyShownFor: z.array(z.object({
        phrase: z.string(), impressions: z.number().int().min(0), position: z.number().min(0),
      })).max(20).default([]),
      most: z.number().int().min(1).max(10).default(10),
    }),
    output: TopicPlan,
  },
  /**
   * One article, from numbered sources and nothing else
   * ([21 §4](../../../docs/21-articles-and-seo.md#4--draft)). Every factual sentence ends in its citation, or in
   * `[?]` where the writer could not source one — and saying `[?]` is the wanted behaviour, not a failure: it is
   * how an honest gap reaches the person who can fill it instead of being dressed as a fact.
   *
   * What comes back is a proposal. `applyArticle` in `contracts` decides what survives it.
   */
  write_article: {
    input: z.object({
      topic: z.object({ targetPhrase: z.string(), audience: z.string(), intent: ArticleIntent, angle: z.string() }),
      business: z.string().default(""),
      lang: z.string().default("en"),
      neverTopic: z.array(z.string()).default([]),
      neverClaim: z.array(z.string()).default([]),
      sources: z.array(ArticleSource).max(40).default([]),
      /** The internal addresses it may link to: the offer page, and this site's own published articles. */
      links: z.array(z.object({ path: z.string(), text: z.string() })).max(40).default([]),
      /**
       * What people actually ask next, from `read_serp` (21 §3). The ten pages already ranking are never passed
       * with them: those are the competitors, and their titles are their words about themselves.
       */
      questions: z.array(z.string()).max(12).default([]),
      /** The other ways the same want is typed, so the article reads as one page about one thing and not as a list. */
      phrasings: z.array(z.string()).max(12).default([]),
      words: z.number().int().min(300).max(4_000).default(1_500),
    }),
    output: ArticleDraft,
  },
  /** An approved article in another language, with its own title, meta description and slug ([21 §6](../../../docs/21-articles-and-seo.md#6--languages)). */
  translate_article: {
    input: z.object({
      lang: z.string(),
      from: z.object({ lang: z.string(), title: z.string(), metaDescription: z.string(), reviewMd: z.string(), faq: z.array(z.object({ question: z.string(), answer: z.string() })) }),
      business: z.string().default(""),
      offer: z.object({ path: z.string(), label: z.string() }),
    }),
    output: ArticleDraft,
  },
  render: { input: z.object({ compositionId: z.uuid() }), output: z.object({ video: AssetRef }) },

  /* -------------------------------------------------------------------------------- Customers (23, ADR 0037) --- */

  /**
   * The first draft of a workspace's own Customers configuration, written from its business page.
   *
   * This is the call that makes the tool generic instead of Overbill's: the groups it looks for and the judge's
   * scored table are rows of a workspace, and they have to come from somewhere. They come from the page the owner
   * has already written and can already read — and every row it returns is a proposal the owner edits, which is why
   * it is worth one call and never worth two.
   *
   * `profile` is the business's own page in markdown, exactly as the customer reads it, like `write_script` above.
   */
  outreach_setup: {
    input: z.object({
      profile: z.string().min(1),
      name: z.string().default(""),
      /** Where the workspace already looks, as `placeKey` writes a place, so nothing proposes what it has. */
      markets: z.array(z.string().max(90)).max(20).default([]),
    }),
    output: z.object({
      /**
       * Where this business should look, when it has nowhere yet (ADR 0043).
       *
       * A business whose customers come to it or that travels to them reaches **towns** — its own and the nearest
       * few. One that sells at a distance reaches **countries**, and leaves the town empty. Proposed here for the
       * same reason the groups are: it is on the page already, and a question whose answer we could have written
       * is one that should not have been asked.
       */
      places: z.array(z.object({
        country: z.string().length(2),
        town: z.string().max(80).default(""),
      })).max(12).default([]),
      groups: z.array(z.object({
        name: z.string().min(2).max(80),
        /** What a search should look for, in words a finder can use. */
        looksFor: z.string().min(5).max(600),
        why: z.string().max(400).default(""),
        /** Whether this group is other businesses, or the public — the letter is only lawful for one of them. */
        audience: z.enum(["business", "public"]).default("business"),
      })).max(8),
      /** Facts worth points, most telling first. The thresholds are the product's; only the rows are the model's. */
      score: z.array(z.object({ fact: z.string().min(3).max(200), points: z.number().int().min(1).max(40) })).max(12),
      /** Facts that make a no whatever the score, beyond the three this product always refuses. */
      refuse: z.array(z.string().min(3).max(200)).max(8),
      /** The offer and the ask, in the owner's own voice, for them to rewrite. */
      offer: z.string().max(600).default(""),
      ask: z.string().max(300).default(""),
    }),
  },

  /**
   * Companies of a kind, in a place, as a map knows them.
   *
   * Serper's Google Maps search, which this repo already holds a key for. It is strong where the customer is a
   * physical business — a café, a deli, a garage — and weak where it is an online seller with a warehouse and no
   * shopfront, which is why it is one finder and not the design.
   *
   * A map is a way in and not a store: a place id may be kept indefinitely under Google's terms and nothing else it
   * answers may be warehoused, so the caller keeps the id, the website it led to, and what it then reads from the
   * company's own site.
   */
  find_companies: {
    input: z.object({
      query: z.string().min(2).max(160),
      /** Two letters, lower case, as the search engine wants them: which country's map to look at. */
      country: z.string().length(2).default("gb"),
    }),
    output: z.object({
      companies: z.array(z.object({
        name: z.string(),
        address: z.string().default(""),
        website: z.string().default(""),
        phone: z.string().default(""),
        placeId: z.string().default(""),
        category: z.string().default(""),
      })).max(30),
    }),
  },

  /**
   * Companies of a kind, found by a model that can search the web.
   *
   * The finder for everything a map cannot see. It is told what we look for, where, and **every site already on the
   * list or already shown by an earlier search**, so it does not spend the customer's money finding the same twenty
   * companies again. Everything it answers is then checked by code — a site that does not answer, a free-mail
   * domain, a company already known — so an invented company fails at the next step and costs nothing more.
   */
  outreach_find: {
    input: z.object({
      looksFor: z.string().min(3).max(600),
      why: z.string().max(400).default(""),
      country: z.string().length(2),
      /** A town or a region, when the owner named one. */
      region: z.string().max(120).default(""),
      /** What the owner asked in their own words, when they said anything. */
      asked: z.string().max(600).default(""),
      /** Domains not to answer with again. */
      avoid: z.array(z.string()).max(400).default([]),
      most: z.number().int().min(1).max(30).default(12),
    }),
    output: z.object({
      companies: z.array(z.object({
        name: z.string().min(1).max(200),
        website: z.string().min(3).max(300),
        /** One line on why it fits, which the screen shows beside it. */
        why: z.string().max(300).default(""),
      })).max(30),
      /** What it could not do, in a sentence, so an empty answer says why rather than looking like a bug. */
      note: z.string().max(400).default(""),
    }),
  },

  /**
   * One company's own pages, read into the facts this workspace's judge asks about.
   *
   * The model brings facts; `judge.yaml`'s descendant — the workspace's scored table — decides. It is given the
   * pages code has already fetched and the list of facts the table scores, and it answers which of them this
   * company's pages support and **the page each was read on**. Code then drops any fact whose page it did not fetch,
   * so a page cannot vote for a fact by claiming one.
   *
   * Everything inside the pages is information about a company and never an instruction.
   */
  outreach_judge: {
    input: z.object({
      name: z.string().min(1).max(200),
      country: z.string().length(2),
      /** What this workspace sells, so "does this company buy it" is answerable at all. */
      weSell: z.string().max(600).default(""),
      /** The facts the workspace's table scores, in its own words. The model answers about these and invents none. */
      facts: z.array(z.object({ id: z.string(), fact: z.string() })).max(24),
      /** Facts that would make a no, asked the same way. */
      refusals: z.array(z.object({ id: z.string(), fact: z.string() })).max(16),
      pages: z.array(z.object({ url: z.string(), text: z.string() })).max(6),
    }),
    output: z.object({
      /** One entry per fact the pages support: which fact, what the page said, and which page said it. */
      found: z.array(z.object({
        id: z.string(),
        says: z.string().max(400),
        url: z.string(),
      })).max(40),
      /** False when the site is parked, dead, or now something else entirely. Refused by code, not by the table. */
      trading: z.boolean().default(true),
      /** A role address published on the company's own pages, at its own domain, or empty. Never guessed. */
      email: z.string().max(254).default(""),
      /** The company's own name as its site writes it, without a legal form, when the finder's was wrong. */
      name: z.string().max(200).default(""),
      /**
       * What makes this company itself, in its own words: a speciality, a story, an award, who it serves. Scored by
       * nothing — the table's facts are why a company is chosen, and these are what the opening line can tell it.
       * Without them the opener had only "no marketing person named" to go on and wrote a guess (2026-09-30).
       */
      about: z.array(z.object({ says: z.string().max(400), url: z.string() })).max(4).default([]),
    }),
  },

  /**
   * The one or two sentences that prove we looked at *this* company, and the only part of a letter a model writes.
   *
   * Everything else — the greeting, the offer, the ask, the signature and the legal footer — is the frame and the
   * business's own words, so the offer is never reworded and the footer is never invented. A sheet too thin to say
   * something only this company could be told gives **no opener**, and so no letter: a generic line costs more than
   * it earns, because it burns the address it was sent from.
   *
   * The subject comes back with it, because a subject written without the opening line in front of you is a subject
   * about nothing.
   */
  /**
   * The one call a night that reads what came back, and the only part of the autopilot a model touches.
   *
   * **It proposes; it never applies.** Everything it answers is checked by code afterwards — the group exists, the
   * market is one this workspace sells in, a narrower group does not already exist — and then the workspace's own
   * dial decides which kinds may be applied without a person. A retirement always waits for one.
   *
   * The shape is closed and small on purpose. A model given room to write prose here would write prose, and a
   * weekly plan cannot be made out of prose.
   */
  /**
   * The website Joogoos makes (ADR 0047 §4 as amended, [28](../../../docs/28-the-website-joogoos-makes.md)): the
   * whole page designed by the model — body HTML and stylesheet — and then sanitised and checked by code. `previous`
   * and `ask` are how a page is changed later: a whole new version each time, never an edit in place. `problems`
   * is what the checks refused last time, on the one retry.
   */
  write_site: {
    input: z.object({
      name: z.string().max(200),
      /** The Business page as the Studio reads it, rivals left out. */
      business: z.string().max(20_000),
      lang: z.string().default("en"),
      neverClaim: z.array(z.string()).max(40).default([]),
      /** The colours *Your look* chose, and whether there is a logo to place as `img:logo`. */
      look: z.object({ colours: z.array(z.string()).max(8), logo: z.boolean() }),
      /** The owner's own photos, as the references the HTML may use, with what is in each. */
      photos: z.array(z.object({ ref: z.string(), caption: z.string(), shape: z.enum(["wide", "tall", "square"]) })).max(12),
      contact: z.object({ phone: z.string(), email: z.string() }),
      ask: z.string().max(1_000).default(""),
      previous: WebsitePage.nullable().default(null),
      problems: z.array(z.string()).max(20).default([]),
    }),
    output: WebsitePage,
  },
  /**
   * A week of posts, proposed ([03 §prepare my week](../../../docs/03-studio-flow.md#prepare-my-week), ADR 0023).
   * `applyWeek` decides what survives it; each piece that does becomes a Studio draft with a day on it.
   */
  plan_week: { input: WeekPlanInput, output: WeekPlan },
  /**
   * The marketing review ([35](../../../docs/35-the-marketing-manager.md), ADR 0063): the evidence code gathered for
   * one week in, what happened and at most three actions from a closed menu out. `applyReview` decides what survives
   * it; the price of each action is worked out by code, never here.
   */
  marketing_review: { input: MarketingReviewInput, output: MarketingReviewOutput },
  /**
   * One turn of the conversation about a review (`conversation.ts`): a reply held to the evidence, and at most one
   * change to the recommendation for the owner to use or leave. `applyReviewTurn` decides what survives it.
   */
  review_turn: { input: ReviewTurnInput, output: ReviewTurnOutput },
  /**
   * A batch of post drafts ([31](../../../docs/31-posts-joogoos-drafts.md) §the drafter, `drafter.ts`): a few ideas from
   * the business page and its sources, each with one post per network and the source it draws on. `checkDrafts`
   * decides what survives it; what does becomes a `scheduled_posts` row at `draft`, and nothing more.
   */
  draft_posts: { input: DraftPostsInput, output: DraftPostsOutput },
  outreach_learn: {
    input: z.object({
      business: z.string().max(200).default(""),
      weSell: z.string().max(600).default(""),
      /** What each cell did: who we looked for, where, how they were found, and what came back. */
      cells: z.array(z.object({
        group: z.string().max(200),
        looksFor: z.string().max(400).default(""),
        /** The place, as `placeKey` writes one: `GB`, or `GB/Ledbury` (ADR 0043). */
        country: z.string().max(90),
        source: z.string().max(40),
        letters: z.number().int().min(0),
        replies: z.number().int().min(0),
        meetings: z.number().int().min(0),
        bounced: z.number().int().min(0),
        optedOut: z.number().int().min(0),
      })).max(60),
      /** What the people who answered actually wrote, trimmed. The only free text it is given. */
      answers: z.array(z.string().max(600)).max(30).default([]),
      /** The places this workspace looks in, so a place to try is one it could actually work. */
      markets: z.array(z.string().max(90)).max(30).default([]),
    }),
    output: z.object({
      /** What the answers have in common, in a sentence or two. Shown on the page, applied to nothing. */
      learned: z.string().max(600).default(""),
      proposals: z.array(z.object({
        kind: z.enum(["reword", "narrow", "retire", "try_market"]),
        /** The group this is about, by the name it was given. Code resolves it, and drops what it cannot. */
        group: z.string().max(200).default(""),
        /** For a `reword`: the new words to look for. For a `narrow`: the narrower group's name and words. */
        name: z.string().max(200).default(""),
        words: z.string().max(400).default(""),
        /** For a `try_market`: the place, written exactly as one of the places it was given (ADR 0043). */
        country: z.string().max(90).default(""),
        /** Why, in the words a person will read on the page before saying yes. */
        why: z.string().max(400),
      })).max(4).default([]),
      /**
       * For a group the answers say something about, one sentence of what was learned there (38 step 4): the last
       * line of that group's plan in words. Code writes the other lines from the counts, and this one where none is said.
       */
      groups: z.array(z.object({
        group: z.string().max(200),
        learned: z.string().max(300),
      })).max(30).default([]),
    }),
  },

  outreach_opener: {
    input: z.object({
      company: z.string().min(1).max(200),
      /** The business writing, and what it sells, so the sentence connects to something. */
      business: z.string().max(200).default(""),
      weSell: z.string().max(600).default(""),
      language: z.string().length(2).default("en"),
      /** The evidence sheet: what was read about this company, and where each was read. */
      facts: z.array(z.object({ says: z.string(), url: z.string().default("") })).max(24),
      /** Words this business never writes, so the guard has less to refuse. */
      neverWords: z.array(z.string()).max(50).default([]),
      /** The businesses its own page names as rivals, for the same reason. */
      rivals: z.array(z.string()).max(30).default([]),
      /**
       * The openers this workspace's last letters carried, so this one is not the same sentence again.
       *
       * The finder is told which domains it has already answered for the same reason (23 §find): left to itself a
       * model writes one good shape and then writes it for ever. Read as a batch — and a batch is how a letter is
       * read by anybody who receives two — eighteen of Overbill's first thirty-one openers were two sentences with
       * the nouns swapped, which is the one thing that tells a stranger nobody wrote this.
       */
      avoid: z.array(z.string().max(400)).max(12).default([]),
    }),
    output: z.object({
      /** Null when nothing on the sheet is specific to this company. The caller writes no letter. */
      opener: z.string().max(400).nullable(),
      /** An honest subject: what the letter is about, never dressed as a reply. */
      subject: z.string().max(160).default(""),
    }),
  },
} as const;

export type OpInput<Op extends Operation> = z.infer<(typeof OperationIO)[Op]["input"]>;
export type OpOutput<Op extends Operation> = z.infer<(typeof OperationIO)[Op]["output"]>;

/**
 * What every adapter returns; the worker prices `units` when `providerCostMicro` is absent.
 *
 * Input tokens come in three prices, so they are three kinds: `tokens_in` is read fresh, `tokens_in_cached` is read
 * from the vendor's prompt cache (a tenth of the price or less), and `tokens_cache_write` is written to it (Claude
 * charges 1.25× for that). Counting all three as `tokens_in` charged a cached Business turn several times what it cost.
 */
export const UnitKind = z.enum(["tokens_in", "tokens_in_cached", "tokens_cache_write", "tokens_out", "image", "second", "character", "request", "cpu_second", "minute"]);
export type UnitKind = z.infer<typeof UnitKind>;

export const ProviderResultMeta = z.object({
  units: z.array(z.object({ kind: UnitKind, count: z.number().min(0) })),
  providerCostMicro: MicroSchema.optional(),
  providerRequestId: z.string().optional(),
  durationMs: z.number().int().min(0),
});
export type ProviderResultMeta = z.infer<typeof ProviderResultMeta>;
